import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteCustomer, getAdminCustomers } from 'apis/adminCustomers';
import { getAdminCustomerCategories } from 'apis/adminCustomerCategories';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const CustomersList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? '' : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('customers', queryParams, getAdminCustomers);
    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery('customer-categories-options', { pageSize: 1000 }, getAdminCustomerCategories);
    const categories = categoriesData?.items || categoriesData?.data?.items || categoriesData?.data || [];
    const deleteMutation = useAdminDeleteMutation('customers', deleteCustomer);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) { toast.success('Xóa thành công'); } else toast.error(res.message);
        } catch { toast.error('Xóa thất bại'); }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        { field: 'name', header: 'Tên' },
        { header: 'Logo', render: (row) => row.logoImage ? <img src={row.logoImage} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4 }} /> : '—' },
        {
            header: 'Loại đối tác / Khách hàng', render: (row) => {
                const catId = row.customerCategoryId || row.CustomerCategoryId;
                const cat = categories.find(c => c.id === catId || c.Id === catId);
                const activeLangId = languages.find(l => l.code === (languageCodeFilter || 'vi'))?.id;
                const t = cat?.translations?.find(tr => tr.languageId === activeLangId) || cat?.translations?.[0];
                return <strong>{t?.name || t?.Name || cat?.name || cat?.Name || (catId ? `Loại #${catId}` : `Menu #${row.navMenuId}`)}</strong>;
            }
        },
        { header: 'Website', render: (row) => row.websiteUrl ? <a href={row.websiteUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6', fontSize: 13 }}>Liên kết ↗</a> : '—' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { header: 'Thao tác', render: (row) => <div className="action-buttons"><button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/customers/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button><button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button></div> },
    ];

    return (
        <div className="admin-page">
            <PageHeader title="Khách hàng & Đối tác" subtitle="Quản lý logo khách hàng và đối tác" action={<Button onClick={() => navigate('/admin/customers/create')}>+ Thêm Khách hàng</Button>} />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item"><Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} /></div>

                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={[...languages.map(l => ({ value: l.code, label: l.name }))]} />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Xóa Khách hàng" message="Bạn có chắc chắn muốn xóa không?" />
        </div>
    );
};

export default CustomersList;
